import React from 'react';
import { Box, BoxProps, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';

import SnackbarProvider from './components/Snackbar/SnackbarProvider';
import { THEMES, THEME_TYPE } from './consts';
import useThemeType from './hooks/useThemeType';
import useMixedThemePageDetector from './hooks/useMixedThemePageDetector';
import PortraitOrientationLock from './components/PortrailOrientationLock';
import ChartThemeProvider from './modules/Chart/core/ChartThemeProvider';
import { chartElementsSx } from './modules/Chart/core/ChartThemeProvider/styles';
import './styles/index.scss';

interface ThemeProviderCoreProps {
  children: React.ReactNode;
  theme: Theme;
  rootNodeSx?: BoxProps['sx'];
}

export const ThemeProviderCore = ({
  children,
  theme,
  rootNodeSx,
}: ThemeProviderCoreProps) => {
  const { isMixedPage } = useMixedThemePageDetector();

  return (
    <MUIThemeProvider theme={theme}>
      <ChartThemeProvider>
        <Box
          sx={(boxTheme): SystemStyleObject<Theme> => ({
            display: 'inline',
            ...(rootNodeSx as SystemStyleObject<Theme> | undefined),
            color: 'neutral.onSurface',
            '& *': {
              fontFamily: 'Lato',
            },
            ...chartElementsSx(boxTheme),
          })}
        >
          <SnackbarProvider>{children}</SnackbarProvider>
          {!isMixedPage && <PortraitOrientationLock />}
        </Box>
      </ChartThemeProvider>
    </MUIThemeProvider>
  );
};

interface ThemeProviderProps {
  children: React.ReactNode;
  themeType?: THEME_TYPE;
  rootNodeSx?: BoxProps['sx'];
  allowDarkMode?: boolean;
}

const ThemeProvider = ({
  children,
  themeType = THEME_TYPE.LIGHT,
  rootNodeSx,
  allowDarkMode = false,
}: ThemeProviderProps) => {
  const currentThemeType = useThemeType(themeType);

  return (
    <ThemeProviderCore
      theme={THEMES[allowDarkMode ? currentThemeType : themeType]}
      rootNodeSx={rootNodeSx}
    >
      {children}
    </ThemeProviderCore>
  );
};

export default ThemeProvider;